import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { AddressBar, Marquee, Mast, StatusBar, WEB, xpCss, webCss } from 'lib/xp';

import pfp from 'assets/pfp.png';
import { fmtAmt } from 'data/epochs';
import { useLedger, fetchPaidTo, isAddress, connectWallet } from 'data/ledger';
import { EXPLORER, short } from 'data/msft';

// PAYDAY.exe — the payroll, as a window.
//  · payroll status: pons holder fee sharing (1% creator tax + 1% base, 1.7% of volume to holders, keeper airdrops)
//  · the pot: chain-derived only. Pre-launch = "awaiting launch". Post-launch the
//    fee session writes distributor reads (pending, epochs) into data/dividends.json; this window never invents a number.
//  · pay-stub generator: NAME ONLY (no bag size, no wallet, no numbers). Amount renders
//    PENDING and the stub itself carries the SAMPLE — NOVELTY line so screenshots that
//    travel without the site still say what they are.


export default function Payday() {
  const [name, setName] = useState(() => {
    try {
      return localStorage.getItem('ic:stub:name') || 'fren';
    } catch (e) {
      return 'fren';
    }
  });
  const [saved, setSaved] = useState(false);
  const canvasRef = useRef(null);
  const { epochs, pendingAmount, paidTotal, asset, sharingPending, live } = useLedger();

  // Your wallet → your real number. Paste an address or connect an injected wallet;
  // we read the MSFT airdrops from pons' distributor to that address straight from the chain.
  const [wallet, setWallet] = useState(() => {
    try {
      return localStorage.getItem('ic:stub:wallet') || '';
    } catch (e) {
      return '';
    }
  });
  const [paid, setPaid] = useState(null);
  const [paidErr, setPaidErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('ic:stub:name', name);
    } catch (e) {
      /* ignore */
    }
  }, [name]);
  useEffect(() => {
    try {
      localStorage.setItem('ic:stub:wallet', wallet);
    } catch (e) {
      /* ignore */
    }
  }, [wallet]);
  useEffect(() => {
    if (!isAddress(wallet)) {
      setPaid(null);
      setPaidErr('');
      return undefined;
    }
    let alive = true;
    setBusy(true);
    setPaidErr('');
    const load = () =>
      fetchPaidTo(wallet)
        .then(p => alive && setPaid(p))
        .catch(() => alive && setPaidErr('could not read the chain — try again in a moment'))
        .finally(() => alive && setBusy(false));
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [wallet]);

  async function onConnect() {
    try {
      const a = await connectWallet();
      if (a) setWallet(a);
    } catch (e) {
      setPaidErr('no wallet found — paste your address instead');
    }
  }

  const paidRounds = epochs.length;
  const launched = paidRounds > 0 || pendingAmount > 0;
  const clean = (name || 'fren').replace(/[<>]/g, '').slice(0, 24) || 'fren';
  const hasPaid = !!paid && isAddress(wallet);
  const payoutsWord = n => `${n} payout${n === 1 ? '' : 's'}`;

  function drawStub() {
    const c = canvasRef.current;
    if (!c) return null;
    const W = 900;
    const H = 560;
    c.width = W;
    c.height = H;
    const g = c.getContext('2d');
    g.fillStyle = '${XP.face}';
    g.fillRect(0, 0, W, H);
    // faint grid
    g.strokeStyle = 'rgba(204,255,0,0.07)';
    for (let x = 0; x < W; x += 44) {
      g.beginPath();
      g.moveTo(x, 0);
      g.lineTo(x, H);
      g.stroke();
    }
    for (let y = 0; y < H; y += 44) {
      g.beginPath();
      g.moveTo(0, y);
      g.lineTo(W, y);
      g.stroke();
    }
    g.strokeStyle = '${XP.ink}';
    g.lineWidth = 3;
    g.strokeRect(18, 18, W - 36, H - 36);
    // title bar
    g.fillStyle = '${XP.ink}';
    g.fillRect(18, 18, W - 36, 44);
    g.fillStyle = '${XP.face}';
    g.font = 'bold 22px Tahoma, Arial, sans-serif';
    g.fillText('PAYDAY.exe  —  pay stub', 34, 48);
    g.font = 'bold 20px Tahoma, Arial, sans-serif';
    g.fillText('✕', W - 52, 47);
    // body
    const rows = [
      ['EMPLOYER', 'INTERNET COIN ($INTERNET)'],
      ['EMPLOYEE', clean],
      ['WALLET', hasPaid ? short(wallet) : '— (enter a wallet for the real number)'],
      ['POSITION', 'holder of the internet'],
      ['PAID IN', 'tokenized MSFT (the pair asset) · airdropped by pons'],
      ['AMOUNT', hasPaid ? `${fmtAmt(paid.total)} MSFT · ${payoutsWord(paid.count)}` : 'PENDING'],
      ['ALL HOLDERS', `${fmtAmt(paidTotal)} MSFT paid across ${paidRounds} epoch${paidRounds === 1 ? '' : 's'}`],
      ['STATUS', hasPaid && paid.count > 0 ? 'paid · on payroll' : 'on payroll'],
    ];
    let y = 100;
    rows.forEach(([k, v]) => {
      g.fillStyle = '${XP.inkDim}';
      g.font = '16px "Lucida Console", "Courier New", monospace';
      g.fillText(k, 44, y);
      g.fillStyle = k === 'AMOUNT' ? '#ffffff' : '${XP.ink}';
      g.font = 'bold 22px Tahoma, Arial, sans-serif';
      g.fillText(v, 230, y);
      g.strokeStyle = '${XP.shadow}';
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(44, y + 14);
      g.lineTo(W - 44, y + 14);
      g.stroke();
      y += 42;
    });
    // disclaimer INSIDE the image
    g.fillStyle = '#ffffff';
    g.font = 'bold 15px Tahoma, Arial, sans-serif';
    g.fillText(
      hasPaid
        ? 'CHAIN-DERIVED · PAST PAYOUTS FROM PONS\' DISTRIBUTOR · NOT A PROMISE OF FUTURE PAYMENT'
        : 'SAMPLE — NOVELTY — NOT A STATEMENT OF EARNINGS OR A PROMISE OF PAYMENT',
      44,
      H - 62,
    );
    g.fillStyle = '${XP.inkDim}';
    g.font = '14px Tahoma, Arial, sans-serif';
    g.fillText('internetcoin.vip · not affiliated with Microsoft · CA: 0x88888cC1c17FfcBa563F418c61596b3fa2Ca2e2c', 44, H - 38);
    return c.toDataURL('image/png');
  }

  function onSave() {
    const url = drawStub();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `paystub-${clean.replace(/\s+/g, '_')}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const shareText = encodeURIComponent(
    hasPaid && paid.count > 0
      ? `the internet paid me ${fmtAmt(paid.total)} MSFT so far. ${payoutsWord(paid.count)}, airdropped by pons, no claiming. (real)\n\ninternetcoin.vip`
      : `i'm on the internet's payroll now. paid in microsoft, around the clock. (real)\n\ninternetcoin.vip`,
  );

  return (
    <Div>
      <div className="xp__menubar">
        <button type="button">File</button>
        <button type="button">Edit</button>
        <button type="button">View</button>
        <button type="button">Help</button>
        <span className="xp__menubar__brand">Internet Coin Payroll</span>
      </div>
      <AddressBar path="payday.internetcoin.vip/payroll.htm" />
      <div className="xp__client web web--felt">
        <Mast
          logo={pfp}
          kicker="Internet Coin Payroll"
          title="PAYDAY.exe"
          right={
            <div className="pd__badge">
              <span className="web__sticker web__sticker--gold">OPEN 24/7</span>
            </div>
          }
        />
        <Marquee>
          ★ the internet has a payroll and it runs around the clock ★ no claiming ★ no dev wallet in
          the loop ★ no volume, no payout ★
        </Marquee>

        <div className="web__body">
          {/* The pot first — it is the number anyone opens this window to see. */}
          <div className="web__panel web__panel--felt web__center pd__pot">
            <div className="web__panel__t">Accrued for holders</div>
            <span className="web__led web__led--big">
              {sharingPending ? 'ACTIVATING…' : launched ? `${fmtAmt(pendingAmount)} ${asset}` : 'AWAITING LAUNCH'}
            </span>
            <div className="web__note pd__pot__fine">
              {paidRounds === 0
                ? 'Chain-derived only: what pons\' distributor holds for holders since the last epoch — never written by hand.'
                : `${fmtAmt(paidTotal)} ${asset} paid across ${paidRounds} epoch${paidRounds === 1 ? '' : 's'} — ${
                    live ? 'read live from the chain' : 'last reading'
                  } · receipts in DIVIDENDS.exe.`}
            </div>
          </div>

          <div className="web__panel web__panel--navy">
            <div className="web__panel__t">How the payroll works</div>
            <table className="web__table pd__terms">
              <tbody>
                <tr>
                  <th scope="row">fee</th>
                  <td>2% per trade — 1% creator tax + pons&apos; 1%</td>
                </tr>
                <tr>
                  <th scope="row">to holders</th>
                  <td>1.7% of every trade, in tokenized MSFT · 0.3% to pons</td>
                </tr>
                <tr>
                  <th scope="row">delivery</th>
                  <td>pons&apos; keeper publishes an epoch and airdrops it — no claiming, no dev wallet in the loop</td>
                </tr>
                <tr>
                  <th scope="row">cadence</th>
                  <td>around the clock, whenever real fees exist</td>
                </tr>
              </tbody>
            </table>
            <div className="web__note">
              No volume, no payout — nothing accrues, nothing is sent · first epoch lands after launch ·
              unclaimed epochs roll forward for 90 days.
            </div>
          </div>

          <div className="web__hr" />

          <div className="web__panel web__panel--white pd__stub">
            <div className="web__panel__t">Print a pay stub</div>

            <div className="pd__stub__row">
              <label htmlFor="pd-name">employee name</label>
              <input
                id="pd-name"
                className="xp__field"
                value={name}
                maxLength={24}
                onChange={e => setName(e.target.value)}
                spellCheck={false}
              />
            </div>
            <div className="pd__stub__row">
              <label htmlFor="pd-wallet">your wallet</label>
              <input
                id="pd-wallet"
                className="xp__field"
                value={wallet}
                placeholder="0x… paste it, or connect"
                maxLength={42}
                onChange={e => setWallet(e.target.value.trim())}
                spellCheck={false}
              />
              <button type="button" className="web__btn web__btn--blue pd__mini" onClick={onConnect}>
                CONNECT
              </button>
            </div>

            {wallet && !isAddress(wallet) && (
              <div className="pd__paid pd__paid--err">that&apos;s not a 0x address, fren</div>
            )}
            {isAddress(wallet) && (
              <div className={`pd__paid${paidErr ? ' pd__paid--err' : ''}`}>
                {busy && !paid ? (
                  'reading the chain…'
                ) : paid ? (
                  <>
                    <b>paid to you</b> {fmtAmt(paid.total)} {asset} · {payoutsWord(paid.count)}
                    {paid.count === 0 && ' · nothing yet — hold through an epoch'}
                    {paid.receipts.length > 0 && (
                      <span className="pd__receipts">
                        {paid.receipts
                          .slice(-3)
                          .reverse()
                          .map(r => (
                            <a key={r.tx} href={`${EXPLORER}/tx/${r.tx}`} target="_blank" rel="noreferrer noopener">
                              {fmtAmt(r.amount)} ↗
                            </a>
                          ))}
                      </span>
                    )}
                  </>
                ) : (
                  paidErr
                )}
              </div>
            )}

            {/* The stub itself stays a little Windows dialog inside the page — it is a document,
                not a web panel, and it is what gets saved as a .png. */}
            <div className="pd__preview">
              <div className="pd__preview__bar">PAYDAY.exe — pay stub</div>
              <div className="pd__preview__body">
                <div>
                  <b>EMPLOYER</b> INTERNET COIN
                </div>
                <div>
                  <b>EMPLOYEE</b> {clean}
                </div>
                <div>
                  <b>WALLET</b> {hasPaid ? short(wallet) : '—'}
                </div>
                <div>
                  <b>POSITION</b> holder of the internet
                </div>
                <div>
                  <b>PAID IN</b> tokenized MSFT
                </div>
                <div>
                  <b>AMOUNT</b>{' '}
                  <span className="pending">
                    {hasPaid ? `${fmtAmt(paid.total)} MSFT · ${payoutsWord(paid.count)}` : 'PENDING'}
                  </span>
                </div>
                <div>
                  <b>ALL HOLDERS</b> {fmtAmt(paidTotal)} MSFT · {paidRounds} epoch{paidRounds === 1 ? '' : 's'}
                </div>
                <div className="pd__preview__note">
                  {hasPaid
                    ? 'CHAIN-DERIVED — PAST PAYOUTS ONLY — NOT A PROMISE OF FUTURE PAYMENT'
                    : 'SAMPLE — NOVELTY — NOT A STATEMENT OF EARNINGS'}
                </div>
              </div>
            </div>

            <div className="web__center pd__btns">
              <button type="button" className="web__btn web__btn--big" onClick={onSave}>
                {saved ? 'SAVED ✓' : '★ SAVE STUB (.PNG) ★'}
              </button>
              <a
                className="web__btn web__btn--blue"
                href={`https://twitter.com/intent/tweet?text=${shareText}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                POST ON X
              </a>
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>

          <div className="web__foot">
            payouts are in a third-party tokenized MSFT product — not Microsoft stock, no
            shareholder rights, no claim on Microsoft&apos;s own dividend · payouts depend entirely on
            trading activity · nothing here is a promise or advice · CA: 0x88888cC1c17FfcBa563F418c61596b3fa2Ca2e2c
          </div>
        </div>
      </div>
      <StatusBar>
        <span>Done</span>
        <span>Internet</span>
      </StatusBar>
    </Div>
  );
}

const Div = styled.div`
  ${xpCss};
  ${webCss};
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .xp__client {
    flex: 1;
    overflow: auto;
    padding: 0;
  }

  .pd__badge {
    display: flex;
    align-items: center;
  }

  .pd__pot .web__led--big {
    font-size: 22px;
  }
  .pd__pot__fine {
    margin-top: 10px;
    text-align: left;
  }

  .pd__terms th {
    width: 24%;
    white-space: nowrap;
  }

  /* form rows: label, field, optional button. XP field chrome comes from xpCss. */
  .pd__stub__row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
    label {
      flex: 0 0 96px;
      color: ${WEB.navy};
      font-weight: bold;
    }
    input {
      flex: 1;
      min-width: 0;
      font-family: 'Courier New', monospace;
    }
  }
  .pd__mini {
    min-width: 0;
    padding: 4px 10px;
    font-size: 10px;
  }

  .pd__paid {
    margin: 8px 0;
    padding: 6px 10px;
    background: #eef8ee;
    border: 1px solid #1e8b3a;
    color: #12481f;
    line-height: 1.5;
    b {
      color: ${WEB.felt};
    }
  }
  .pd__paid--err {
    background: #fff0f1;
    border-color: ${WEB.red};
    color: #7a0a1c;
  }
  .pd__receipts {
    display: inline-flex;
    gap: 8px;
    margin-left: 8px;
    a {
      color: ${WEB.blue};
    }
  }

  /* The pay stub is a document, not a page section: a little Windows dialog with a navy title
     bar and a white body, so it still reads as something you would print. */
  .pd__preview {
    margin: 12px 0;
    border: 2px solid;
    border-color: #fff ${WEB.navy} ${WEB.navy} #fff;
    box-shadow: 2px 2px 0 rgba(0, 0, 0, 0.3);
    background: #fff;
  }
  .pd__preview__bar {
    padding: 4px 10px;
    background: linear-gradient(180deg, ${WEB.navyLight}, ${WEB.navy});
    color: ${WEB.gold};
    font: bold 11px Tahoma, sans-serif;
    letter-spacing: 1px;
  }
  .pd__preview__body {
    padding: 10px 12px;
    font-family: 'Courier New', monospace;
    font-size: 11.5px;
    line-height: 1.75;
    color: ${WEB.ink};
    b {
      display: inline-block;
      min-width: 104px;
      color: ${WEB.navy};
    }
    .pending {
      background: ${WEB.gold};
      color: ${WEB.goldInk};
      padding: 0 6px;
      font-weight: bold;
    }
  }
  .pd__preview__note {
    margin-top: 8px;
    padding-top: 7px;
    border-top: 1px dashed ${WEB.goldDeep};
    font-size: 9.5px;
    letter-spacing: 0.5px;
    color: #6b5a1f;
  }

  .pd__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
    margin-top: 12px;
    a.web__btn {
      text-decoration: none;
      display: inline-block;
    }
  }

  @media (max-width: 460px) {
    .pd__stub__row {
      flex-wrap: wrap;
      label {
        flex: 1 0 100%;
      }
    }
    .pd__preview__body b {
      min-width: 0;
      display: block;
    }
  }
`;